"use client";

import { ArrowRight, TriangleAlert } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/ui/link";
import { newsvendorOrderQty } from "@/lib/bf-toolkit/calculations";
import type { DemandSpread } from "@/lib/bf-toolkit/calculations";
import { cn } from "@/lib/utils";

import {
  CONFIDENCE_OPTIONS,
  DEFAULTS,
  SALVAGE_OPTIONS,
  salvageValueFor,
  STRINGS,
} from "./strings";
import type { SalvageTier } from "./strings";

interface OrderQuantityProps {
  /** Request locale, used only for number and currency formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the input prefix (e.g. "$"). */
  currencySymbol: string;
}

// Bump the number text well above the Input default so the figures a founder
// types read clearly. twMerge resolves the font-size conflict.
const INPUT_TEXT = "text-heading-20";

/**
 * Parse a number the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can show a neutral prompt rather
 * than a bogus result.
 */
function parseNumber(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** One demand scenario's outcome at the recommended order quantity. */
interface Scenario {
  label: string;
  demand: number;
  sold: number;
  leftover: number;
  short: number;
  /** Money sunk into unsold units you can't recover: `leftover × (cost - salvage)`. */
  leftoverLoss: number;
  /** Cash the leftover units bring back at their salvage value: `leftover × salvage`.
   * Surfaced only in the no-downside case (salvage ≥ cost), where there is no
   * `leftoverLoss` to show but the leftovers still recover more than they cost. */
  leftoverRecovered: number;
  /** Margin forgone on demand you couldn't fill: `short × (price - cost)`. */
  missedMargin: number;
  profit: number;
}

function outcomeAt(
  order: number,
  demand: number,
  price: number,
  cost: number,
  salvage: number,
  label: string
): Scenario {
  const sold = Math.min(order, demand);
  const leftover = Math.max(order - demand, 0);
  const short = Math.max(demand - order, 0);
  // Revenue on what sold, less the cost of everything ordered, plus whatever the
  // leftover units recover.
  const profit = sold * price - order * cost + leftover * salvage;
  // The downside of getting the order wrong, each way: cash stuck in unsold
  // stock, and margin missed on demand you couldn't fill.
  const leftoverLoss = leftover * Math.max(cost - salvage, 0);
  const leftoverRecovered = leftover * salvage;
  const missedMargin = short * Math.max(price - cost, 0);
  return {
    label,
    demand,
    sold,
    leftover,
    short,
    leftoverLoss,
    leftoverRecovered,
    missedMargin,
    profit,
  };
}

export function OrderQuantity({
  locale,
  currency,
  currencySymbol,
}: OrderQuantityProps) {
  const [demandStr, setDemandStr] = useState<string>(DEFAULTS.demand);
  const [spread, setSpread] = useState<DemandSpread>(DEFAULTS.spread);
  const [priceStr, setPriceStr] = useState<string>(DEFAULTS.price);
  const [costStr, setCostStr] = useState<string>(DEFAULTS.cost);
  const [salvageTier, setSalvageTier] = useState<SalvageTier>(DEFAULTS.salvage);
  const [orderStepStr, setOrderStepStr] = useState<string>("");

  const demand = parseNumber(demandStr);
  const price = parseNumber(priceStr);
  const cost = parseNumber(costStr);
  const orderStep = parseNumber(orderStepStr);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
    return (value: number) => nf.format(value);
  }, [locale, currency]);

  const formatUnits = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return (value: number) => nf.format(value);
  }, [locale]);

  const state = useMemo(() => {
    // Need a real demand and real price/cost to say anything.
    if (
      !Number.isFinite(demand) ||
      demand <= 0 ||
      !Number.isFinite(price) ||
      !Number.isFinite(cost)
    ) {
      return { kind: "empty" as const };
    }

    const salvage = salvageValueFor(salvageTier, price, cost);
    // Passing the order step lets the maths pick the profit-optimal multiple
    // (which may round the optimum down), rather than blindly rounding up.
    const result = newsvendorOrderQty(
      demand,
      spread,
      cost,
      price,
      salvage,
      Number.isInteger(orderStep) && orderStep >= 1 ? orderStep : undefined
    );

    if (result.status === "no-margin") {
      return { kind: "no-margin" as const };
    }

    const order = result.recommendedOrder;
    const scenarios: [Scenario, Scenario, Scenario] = [
      outcomeAt(order, result.demandLow, price, cost, salvage, STRINGS.weaker),
      outcomeAt(
        order,
        result.demandExpected,
        price,
        cost,
        salvage,
        STRINGS.expected
      ),
      outcomeAt(
        order,
        result.demandHigh,
        price,
        cost,
        salvage,
        STRINGS.stronger
      ),
    ];

    return {
      kind: "ok" as const,
      order,
      unconstrainedOrder: result.unconstrainedOrder,
      stepped: order !== result.unconstrainedOrder,
      step: orderStep,
      scenarios,
      overstockFree: result.status === "overstock-free",
    };
  }, [demand, spread, price, cost, salvageTier, orderStep]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real result. Raw money is intentionally not sent.
  const posthog = usePostHog();
  const orderForAnalytics = state.kind === "ok" ? state.order : null;
  const overstockFree = state.kind === "ok" && state.overstockFree;
  useEffect(() => {
    if (orderForAnalytics === null) return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "order-quantity",
        spread,
        salvageTier,
        overstockFree,
        stepped: Number.isInteger(orderStep) && orderStep >= 1,
        recommendedOrder: orderForAnalytics,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [
    posthog,
    spread,
    salvageTier,
    overstockFree,
    orderStep,
    orderForAnalytics,
  ]);

  const isWarning = state.kind === "no-margin";
  const showMobileResultBar = state.kind !== "empty";

  return (
    <div
      className={cn(
        "border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7",
        showMobileResultBar && "pt-16 sm:pt-7"
      )}
    >
      {showMobileResultBar && (
        <div
          aria-live="polite"
          data-mobile-result-bar
          className={cn(
            "border-grey-100 fixed inset-x-0 top-[var(--nav-offset,64px)] z-40 border-b px-4 py-3 shadow-sm sm:hidden",
            isWarning ? "bg-red-50" : "bg-pulse-50"
          )}
        >
          {state.kind === "no-margin" ? (
            <p className="text-body-16 font-semibold text-red-700">
              Each unit sells at or below cost.
            </p>
          ) : (
            <p className="text-heading-16 text-[#0D0D0D]">
              {STRINGS.orderPrefix}{" "}
              <span className="text-pulse-700">
                {formatUnits(state.order)} {STRINGS.units}
              </span>
              .
            </p>
          )}
        </div>
      )}
      {/* Inputs */}
      <div className="flex flex-col gap-5">
        <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
          {STRINGS.toolTitle}
        </h2>

        {/* The four figures tile four-across so each box stays narrow instead
            of stretching a half-column; the two choice groups share the row
            below. */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
          <NumberField
            label={STRINGS.demand}
            value={demandStr}
            onChange={setDemandStr}
          />
          <NumberField
            label={STRINGS.orderStep}
            value={orderStepStr}
            placeholder={STRINGS.orderStepPlaceholder}
            onChange={setOrderStepStr}
          />
          <MoneyField
            label={STRINGS.price}
            value={priceStr}
            symbol={currencySymbol}
            onChange={setPriceStr}
          />
          <MoneyField
            label={STRINGS.cost}
            value={costStr}
            symbol={currencySymbol}
            onChange={setCostStr}
          />
          <div className="col-span-2">
            <ChipGroup
              label={STRINGS.confidence}
              options={CONFIDENCE_OPTIONS}
              value={spread}
              onChange={setSpread}
            />
          </div>
          <div className="col-span-2">
            <ChipGroup
              label={STRINGS.salvage}
              options={SALVAGE_OPTIONS}
              value={salvageTier}
              onChange={setSalvageTier}
            />
          </div>
        </div>
      </div>

      {/* Output */}
      <div
        aria-live="polite"
        className={cn(
          "mt-6 rounded-xl p-5 sm:p-6",
          isWarning ? "bg-red-50" : "bg-pulse-50"
        )}
      >
        {state.kind === "empty" && (
          <p className="text-body-16 text-text-label">
            Enter your expected demand, selling price and unit cost to see how
            many units to order.
          </p>
        )}

        {state.kind === "no-margin" && (
          <div className="hidden sm:block">
            <div className="flex items-start gap-3">
              <TriangleAlert
                className="mt-1 size-6 shrink-0 text-red-600"
                aria-hidden="true"
              />
              <p className="text-heading-24 lg:text-heading-32 text-red-700">
                Each unit sells at or below cost.
              </p>
            </div>
            <p className="text-body-16 mt-3 text-red-700">
              At {formatCurrency(price)} against a {formatCurrency(cost)} cost
              there&rsquo;s no margin to earn by stocking, so there&rsquo;s no
              order quantity to recommend. Raise your price or lower your cost.
            </p>
          </div>
        )}

        {state.kind === "ok" && (
          <div>
            <p className="text-heading-24 lg:text-heading-32 hidden text-[#0D0D0D] sm:block">
              {STRINGS.orderPrefix}{" "}
              <span className="text-pulse-700">
                {formatUnits(state.order)} {STRINGS.units}
              </span>
              .
            </p>
            {state.stepped && (
              <p className="text-body-14 text-text-label mt-2">
                Fitted to your {formatUnits(state.step)}-unit order increment,
                the choice that keeps the most profit. The unconstrained optimum
                is {formatUnits(state.unconstrainedOrder)} units.
              </p>
            )}
            <DemandChart
              scenarios={state.scenarios}
              order={state.order}
              formatUnits={formatUnits}
            />
            <p className="text-body-16 text-text-body mt-3">
              {readFor(state.scenarios, state.overstockFree, formatUnits)}
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {state.scenarios.map((scenario) => (
                <ScenarioCard
                  key={scenario.label}
                  scenario={scenario}
                  formatCurrency={formatCurrency}
                  formatUnits={formatUnits}
                />
              ))}
            </div>

            <Link
              href={`/tools/financing-break-even?price=${price}&cost=${cost}&purchase=${Math.round(
                state.order * cost
              )}`}
              className="text-body-14 text-pulse-700 mt-5 inline-flex items-center gap-1.5 font-semibold underline underline-offset-2 hover:text-blue-800"
            >
              Can you fund this order? See the financing break-even
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-3xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-7 w-auto shrink-0 md:h-8 md:w-auto"
        />
      </div>
    </div>
  );
}

/** The plain-language read under the headline: how the single order holds up at
 * the weak and strong ends of the demand range. */
function readFor(
  scenarios: [Scenario, Scenario, Scenario],
  overstockFree: boolean,
  formatUnits: (v: number) => string
): string {
  const [weaker, , stronger] = scenarios;

  if (overstockFree) {
    return "Leftover units recover at least what they cost you, so there's no downside to stocking up. This order covers even your strongest scenario.";
  }

  const weakerClause =
    weaker.profit > 0
      ? `If demand is weak (${formatUnits(weaker.demand)} units) you'd sell ${formatUnits(weaker.sold)} and still turn a profit`
      : `If demand is weak (${formatUnits(weaker.demand)} units) the ${formatUnits(weaker.leftover)} units left over would put you in the red`;

  const strongerClause =
    stronger.short > 0
      ? `if it runs strong (${formatUnits(stronger.demand)}) you'd sell out and miss about ${formatUnits(stronger.short)} units of demand.`
      : `and you'd still meet demand even if it runs strong (${formatUnits(stronger.demand)}).`;

  return `${weakerClause}; ${strongerClause}`;
}

/**
 * A compact "demand vs. your order" chart: one bar per demand scenario with the
 * recommended order drawn as a marked vertical line across all three. The blue
 * part of each bar is demand you'd fill; a red tail is demand you'd miss when a
 * scenario runs past the order; the faint gap up to the line is stock left over.
 */
function DemandChart({
  scenarios,
  order,
  formatUnits,
}: {
  scenarios: [Scenario, Scenario, Scenario];
  order: number;
  formatUnits: (v: number) => string;
}) {
  const W = 340;
  const padL = 64;
  const padR = 12;
  const padT = 24;
  const rowSlot = 26;
  const barH = 12;
  const rows = scenarios.length;
  const H = padT + rows * rowSlot + 6;

  const maxDemand = Math.max(...scenarios.map((s) => s.demand));
  const maxX = Math.max(order, maxDemand) * 1.08;
  const plotW = W - padL - padR;
  const x = (u: number) => padL + (Math.max(u, 0) / maxX) * plotW;

  const orderX = x(order);
  // Keep the order label inside the viewbox: flip it left of the line when the
  // line sits in the right portion of the plot.
  const labelRight = orderX > W * 0.62;

  const shortLabels = ["Weaker", "Expected", "Stronger"];

  return (
    <figure className="m-0 mt-4">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <ChartLegend color="var(--pulse-700)" label="Sold" />
        <ChartLegend color="var(--pulse-300)" label="Leftover" />
        <ChartLegend color="var(--red-600)" label="Short" />
        <ChartLegend color="var(--pulse-700)" label="Your order" line />
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`How ordering ${formatUnits(order)} units covers each demand scenario.`}
      >
        {scenarios.map((scenario, i) => {
          const yTop = padT + i * rowSlot;
          const yBar = yTop + (rowSlot - barH) / 2;
          const sold = Math.min(scenario.demand, order);
          const hasShort = scenario.demand > order;
          return (
            <g key={scenario.label}>
              <text
                x={padL - 8}
                y={yBar + barH - 2}
                textAnchor="end"
                className="text-[10px]"
                fill="var(--grey-700)"
              >
                {shortLabels[i]}
              </text>
              <rect
                x={padL}
                y={yBar}
                width={plotW}
                height={barH}
                rx={3}
                fill="var(--grey-100)"
              />
              <rect
                x={padL}
                y={yBar}
                width={x(sold) - padL}
                height={barH}
                rx={3}
                fill="var(--pulse-700)"
              />
              {scenario.demand < order && (
                <rect
                  x={x(sold)}
                  y={yBar}
                  width={orderX - x(sold)}
                  height={barH}
                  rx={3}
                  fill="var(--pulse-300)"
                />
              )}
              {hasShort && (
                <rect
                  x={orderX}
                  y={yBar}
                  width={x(scenario.demand) - orderX}
                  height={barH}
                  rx={3}
                  fill="var(--red-600)"
                />
              )}
            </g>
          );
        })}
        <line
          x1={orderX}
          x2={orderX}
          y1={padT - 6}
          y2={padT + rows * rowSlot}
          stroke="var(--pulse-700)"
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
        <circle cx={orderX} cy={padT - 6} r={3} fill="var(--pulse-700)" />
        <text
          x={orderX + (labelRight ? -7 : 7)}
          y={padT - 11}
          textAnchor={labelRight ? "end" : "start"}
          className="text-[10px] font-semibold"
          fill="var(--pulse-700)"
        >
          Order {formatUnits(order)}
        </text>
      </svg>
    </figure>
  );
}

function ChartLegend({
  color,
  label,
  line,
}: {
  color: string;
  label: string;
  line?: boolean;
}) {
  return (
    <span className="text-label-11 text-grey-700 inline-flex items-center gap-1.5">
      {line ? (
        <span
          aria-hidden="true"
          className="inline-block w-4 border-t-2 border-dashed"
          style={{ borderColor: color }}
        />
      ) : (
        <span
          aria-hidden="true"
          className="inline-block size-2.5 rounded-sm"
          style={{ background: color }}
        />
      )}
      {label}
    </span>
  );
}

function ScenarioCard({
  scenario,
  formatCurrency,
  formatUnits,
}: {
  scenario: Scenario;
  formatCurrency: (v: number) => string;
  formatUnits: (v: number) => string;
}) {
  const { leftover, short } = scenario;
  const sellsThrough = leftover === 0 && short === 0;

  return (
    <div className="bg-surface border-pulse-100 flex h-full flex-col rounded-xl border p-4">
      <p className="text-label-11 text-grey-400 uppercase">{scenario.label}</p>
      <p className="text-body-14 text-text-label mt-0.5">
        {STRINGS.demandRow} {formatUnits(scenario.demand)} {STRINGS.units}
      </p>

      <dl className="mt-3 flex flex-col gap-1.5">
        <ScenarioRow
          label={STRINGS.soldRow}
          value={`${formatUnits(scenario.sold)} ${STRINGS.units}`}
        />
        {leftover > 0 && (
          <>
            <ScenarioRow
              label={STRINGS.leftOverRow}
              value={`${formatUnits(leftover)} ${STRINGS.units}`}
            />
            {scenario.leftoverLoss > 0 ? (
              <ScenarioRow
                label={STRINGS.leftoverCostRow}
                value={`-${formatCurrency(scenario.leftoverLoss)}`}
                critical
              />
            ) : scenario.leftoverRecovered > 0 ? (
              <ScenarioRow
                label={STRINGS.leftoverRecoveredRow}
                value={`+${formatCurrency(scenario.leftoverRecovered)}`}
                positive
              />
            ) : null}
          </>
        )}
        {short > 0 && (
          <>
            <ScenarioRow
              label={STRINGS.shortRow}
              value={`${formatUnits(short)} ${STRINGS.units}`}
            />
            <ScenarioRow
              label={STRINGS.missedMarginRow}
              value={`-${formatCurrency(scenario.missedMargin)}`}
              critical
            />
          </>
        )}
        {sellsThrough && (
          <p className="text-body-14 text-green-700">{STRINGS.sellsThrough}</p>
        )}
      </dl>

      <div className="border-pulse-100 mt-auto border-t pt-3">
        <ScenarioRow
          label={STRINGS.profitRow}
          value={formatCurrency(scenario.profit)}
          strong
          critical={scenario.profit < 0}
        />
      </div>
    </div>
  );
}

function ScenarioRow({
  label,
  value,
  strong,
  critical,
  positive,
}: {
  label: string;
  value: string;
  strong?: boolean;
  critical?: boolean;
  positive?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt
        className={cn(
          "text-body-14",
          strong ? "font-semibold text-[#0D0D0D]" : "text-text-label"
        )}
      >
        {label}
      </dt>
      <dd
        className={cn(
          strong ? "text-body-16 font-semibold" : "text-body-14 font-medium",
          critical
            ? "text-red-700"
            : positive
              ? "text-green-700"
              : "text-[#0D0D0D]"
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <span className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-label={label}
        className="flex flex-wrap gap-2"
      >
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                "text-body-14 focus-visible:ring-pulse-500 rounded-full border px-4 py-2 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
                active
                  ? "border-pulse-700 bg-pulse-700 text-white"
                  : "border-grey-100 text-grey-700 hover:text-black"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <Input
        id={id}
        inputMode="numeric"
        aria-label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`placeholder:text-grey-400 h-14 max-w-[9rem] ${INPUT_TEXT}`}
      />
    </div>
  );
}

function MoneyField({
  label,
  value,
  symbol,
  onChange,
}: {
  label: string;
  value: string;
  symbol: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <div className="relative">
        <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-14 max-w-[9rem] pl-9 ${INPUT_TEXT}`}
        />
      </div>
    </div>
  );
}
